/**
 * 印刷設定の解決（設定 → 表示項目・用紙・縮尺）
 */
import { PrintSetting } from './types';
import { ViewRange, halfMonthRange, monthRange } from './time';

export const PRINT_ITEMS: Array<{ key: string; label: string }> = [
  { key: 'assignment', label: '割り当て（パターン・時間）' },
  { key: 'hope', label: '希望' },
  { key: 'business', label: '業務' },
  { key: 'break', label: '休憩' },
  { key: 'supportOrg', label: '支援先' },
  { key: 'patternLegend', label: 'パターンの凡例' },
  { key: 'headcount', label: '人数行' },
  { key: 'totals', label: '合計列' },
  { key: 'laborCost', label: '人件費' },
];

export const DEFAULT_PRINT_SETTING: PrintSetting = {
  period: 'current',
  dateFrom: null,
  dateTo: null,
  paper: 'A4',
  orientation: 'landscape',
  fontSize: 'large',
  fitToWidth: true,
  items: ['assignment', 'headcount', 'totals', 'patternLegend'],
  monochrome: false,
  thickBorder: true,
  borderColor: '#333333',
  stripeColor: null,
  marginTop: true,
  marginLeft: true,
  marginRight: true,
  headerScale: 100,
  staffNameScale: 100,
  splitByBusiness: false,
  header: true,
};

/** 文字サイズの倍率の選択肢（%） */
export const PRINT_SCALES = [80, 90, 100, 110, 125, 150];

/**
 * パターン名が時刻そのもの（「9:00-18:15」「９：００－１８：１５」など）か
 *
 * 名前と時刻を並べると同じ時間が 2 回出るので、そのときは時刻を出さないために使う（印刷の凡例・パレット）。
 * 全角を半角にし、時の先頭の 0 を落としてから数字だけで比べる（9/24）。
 */
export const nameIsTime = (
  name: string | null | undefined, start: string | null | undefined, end: string | null | undefined,
): boolean => {
  if (!name || !start || !end) {
    return false;
  }
  const digits = (s: string) => s.normalize('NFKC').replace(/(^|\D)0(\d)/g, '$1$2').replace(/[^0-9]/g, '');
  return digits(name) === digits(`${start}-${end}`);
};

export const resolvePrintSetting = (partial: Partial<PrintSetting> | null | undefined): PrintSetting => ({
  ...DEFAULT_PRINT_SETTING,
  ...(partial || {}),
  items: partial && partial.items ? partial.items : DEFAULT_PRINT_SETTING.items,
});

/** 印刷期間 */
export const resolvePrintRange = (setting: PrintSetting, current: ViewRange, monthStartDay: number): ViewRange => {
  if (setting.period === 'custom' && setting.dateFrom && setting.dateTo) {
    return setting.dateFrom <= setting.dateTo
      ? { from: setting.dateFrom, to: setting.dateTo }
      : { from: setting.dateTo, to: setting.dateFrom };
  }
  if (setting.period === 'half') {
    return halfMonthRange(current.from);
  }
  if (setting.period === 'month') {
    return monthRange(current.from, monthStartDay);
  }
  return current;
};

export const fontSizePx = (size: PrintSetting['fontSize']): number => {
  // 掲示を離れて読めるよう 9/24 に 1〜2px ずつ大きくした（旧 9 / 11 / 13）
  if (size === 'small') {
    return 10;
  }
  if (size === 'medium') {
    return 12;
  }
  return 14;
};

export const hasItem = (setting: PrintSetting, key: string): boolean => setting.items.indexOf(key) !== -1;

/** #RRGGBB の輝度から文字色（黒／白）を決める */
export const textColorFor = (bg: string | null | undefined): string => {
  if (!bg || !/^#[0-9A-Fa-f]{6}$/.test(bg)) {
    return '#333333';
  }
  const r = parseInt(bg.substr(1, 2), 16);
  const g = parseInt(bg.substr(3, 2), 16);
  const b = parseInt(bg.substr(5, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#333333' : '#FFFFFF';
};

/** パターン色が無いときの既定色（ID からハッシュで安定させる） */
const PALETTE = ['#E3F2E8', '#E7ECF4', '#FDF1DC', '#F5DCE2', '#E0F4F4', '#EFE6F7', '#F8EBD9', '#E6F0FB'];
export const defaultPatternColor = (id: string | null): string => {
  if (!id) {
    return '#F5F5F5';
  }
  let h = 0;
  for (let i = 0; i < id.length; i += 1) {
    h = (h * 31 + id.charCodeAt(i)) % 100000;
  }
  return PALETTE[h % PALETTE.length];
};
